import * as Actions from '../../../Modules/CmsActions'
import type { IPagination } from '../../../Types'
import type {
  IColor,
  IFilteredPostRes,
  IFontList,
  IFontSet,
  IPostTypeRes,
  IShape,
  ITaxonomyRes,
} from '../../../Types/Api/Page'

interface ILayout {
  id: string
  title: string
  category_ids: { id: string; name: string }[]
}

interface State {
  loading: boolean
  updating: boolean
  previewing: boolean
  edit: any
  list: Array<{ name: string; downloadURL: string }>
  conditions: object | null
  pagination: IPagination
  layout: string
  components: any
  currentSectionIndex: null | number
  idInputValue: string
  editFlag: boolean
  alertFlag: boolean
  sectionListIndex: number
  sections: Array<any>
  sectionsPreviews: {
    [key: string]: {
      /** プレビュー用のCSS URL */
      css: string
    }
  }
  header: any
  footer: any
  parts: Array<any>
  layouts: {
    loading: boolean
    creating: boolean
    original: ILayout[]
    template: ILayout[]
    categories: { id: string; name: string }[]
    html: {
      loading: boolean
      template: string
    }
  }
  colors: IColor[]
  color_set_id?: number
  ja_font_family_id?: number
  en_font_family_id?: number
  fonts?: IFontList
  shapes: IShape[]
  post_types?: IPostTypeRes[]
  taxonomies?: ITaxonomyRes[]
  posts?: IFilteredPostRes[]
  custom_posts?: IFilteredPostRes[]
  font_sets: IFontSet[]
  mobileViewMode: {
    original: boolean
    reused: boolean
  }
}

const initialState: State = {
  loading: true,
  updating: false,
  previewing: false,
  edit: {
    id: 0,
    status: 'publish',
    title: '',
    url: '',
    is_show_sitemap: true,
    layout_values: {
      seo_title: '',
      seo_description: '',
      canonical_url: '',
      noindex: false,
      nofollow: false,
      structured_data: '',
      og_title: '',
      og_description: '',
      og_locale: '',
      og_image: '',
      twitter_og_title: '',
      twitter_og_description: '',
      twitter_og_image_url: '',
      favicon_url: '',
    },
    meta: {
      embed_type: 'postscript',
      before_head_close_tag: '',
      after_body_open_tag: '',
      before_body_close_tag: '',
      cookie_before_head_close_tag: '',
      cookie_after_body_open_tag: '',
      cookie_before_body_close_tag: '',
    },
    fallback_seo: {
      seo_title: '',
      seo_description: '',
    },
  },
  list: [],
  conditions: { title: null },
  pagination: {
    current_page: 1,
    first_page_url: null,
    from: 0,
    last_page: 1,
    last_page_url: null,
    next_page_url: null,
    path: null,
    per_page: 15,
    prev_page_url: null,
    to: 0,
    total: 0,
  },
  layout: '',
  components: '',
  currentSectionIndex: null,
  idInputValue: '',
  editFlag: false,
  alertFlag: false,
  sectionListIndex: 0,
  sections: [],
  sectionsPreviews: {},
  parts: [],
  header: {},
  footer: {},
  layouts: {
    loading: false,
    creating: false,
    original: [],
    template: [],
    categories: [],
    html: {
      loading: false,
      template: '',
    },
  },
  colors: [],
  shapes: [],
  font_sets: [],
  mobileViewMode: {
    original: false,
    reused: false,
  },
}

const UIPageReducer = (state = initialState, action) => {
  const { type, payload, error } = action

  switch (type) {
    case Actions.REQUEST_FETCH_ALL_PAGE:
      return { ...state, loading: true }

    case Actions.REQUEST_FETCH_PAGE_LAYOUT_TEMPLATE:
      return {
        ...state,
        layouts: {
          ...state.layouts,
          html: { ...state.layouts.html, loading: true },
        },
      }

    case Actions.FINISH_FETCH_PAGE_LAYOUT_TEMPLATE:
      return {
        ...state,
        layouts: {
          ...state.layouts,
          html: {
            ...state.layouts.html,
            loading: false,
            template: payload.template,
          },
        },
      }

    case Actions.FAIL_FETCH_PAGE_LAYOUT_TEMPLATE:
      return {
        ...state,
        layouts: {
          ...state.layouts,
          html: { ...state.layouts.html, loading: false },
        },
      }

    case Actions.REQUEST_FETCH_PAGE_LAYOUTS:
      return { ...state, layouts: { ...state.layouts, loading: true } }

    case Actions.REQUEST_CREATE_PAGE_LAYOUT:
      return { ...state, layouts: { ...state.layouts, creating: true } }

    case Actions.FINISH_CREATE_PAGE_LAYOUT:
      return { ...state, layouts: { ...state.layouts, creating: false } }

    case Actions.FAIL_FETCH_PAGE_LAYOUTS:
      return { ...state, layouts: { ...state.layouts, loading: false } }

    case Actions.FINISH_FETCH_PAGE_LAYOUTS:
      return {
        ...state,
        layouts: {
          ...state.layouts,
          loading: false,
          original: payload.original,
          template: payload.template,
          categories: payload.categories,
        },
      }

    case Actions.REQUEST_CHANGE_CONDITION_PAGE:
      return updateCondition(state, action.payload.text)

    case Actions.FINISH_FETCH_ALL_PAGE:
      return error
        ? { ...state, loading: false }
        : {
            ...state,
            loading: false,
            list: payload.pages,
            hasFallbackPage: payload.hasFallbackPage,
            pagination: payload.pagination,
          }

    case Actions.REQUEST_FETCH_PAGE:
      return { ...state, loading: true }

    case Actions.SET_PAGE_SHAPE_MODE:
      return { ...state, shape_mode: action.mode }

    case Actions.SET_PAGE_COLOR_SET_ID:
      return { ...state, color_set_id: action.id }

    case Actions.SET_PAGE_FONT_ID:
      return action.payload.type === 'ja'
        ? { ...state, ja_font_family_id: action.payload.id ?? null }
        : { ...state, en_font_family_id: action.payload.id ?? null }

    case Actions.FINISH_FETCH_PAGE:
      return {
        ...state,
        loading: false,
        edit: {
          ...state.edit,
          ...payload.edit,
          layout_values: {
            ...state.edit.layout_values,
            ...payload.edit.layout_values,
          },
          meta: { ...state.edit.meta, ...payload.edit.meta },
          fallback_seo: {
            ...state.edit.fallback_seo,
            ...payload.edit.fallback_seo,
          },
        },
        sections: payload.sections,
        section_previews: payload.section_previews,
        header: payload.header,
        footer: payload.footer,
        layout: payload.layout,
        layoutValues: payload.layoutValues,
        components: payload.components,
        parts: payload.parts,
        colors: payload.colors,
        color_set_id: payload.color_set_id,
        ja_font_family_id: payload.ja_font_family_id,
        en_font_family_id: payload.en_font_family_id,
        fonts: payload.fonts,
        shapes: payload.shapes,
        shape_mode: payload.shape_mode,
        font_sets: payload.font_sets,
      }

    case Actions.UNLOAD_PAGE:
      return {
        ...state,
        edit: initialState.edit,
        sections: initialState.sections,
        section_previews: initialState.sectionsPreviews,
        header: initialState.header,
        footer: initialState.footer,
        layout: initialState.layout,
        layoutValues: {},
        components: initialState.components,
        parts: initialState.parts,
        mobileViewMode: initialState.mobileViewMode,
      }

    case Actions.REQUEST_UPDATE_PAGE:
      return { ...state, updating: true }

    case Actions.RESET_UPDATE_PAGE:
      return { ...state, updating: false }

    case Actions.REQUEST_PREVIEW_PAGE:
      return { ...state, previewing: true }

    case Actions.RESET_PREVIEW_PAGE:
      return { ...state, previewing: false }

    case Actions.UPDATE_SECTION_VALUE:
      return {
        ...state,
        sections: payload.sections,
      }

    case Actions.ADD_SECTIONS:
      return {
        ...state,
        sections: payload.sections,
      }

    case Actions.DELETE_SECTIONS:
      return {
        ...state,
        sections: payload.sections,
      }

    case Actions.UPDATE_SECTIONS:
      return {
        ...state,
        sections: payload.sections,
      }

    case Actions.GET_SECTIONS_INDEX:
      return {
        ...state,
        currentSectionIndex: action.payload,
      }

    case Actions.GET_ID_INPUT_VALUE:
      return {
        ...state,
        idInputValue: action.payload,
      }

    case Actions.RESET_ID_INPUT_VALUE:
      return {
        ...state,
        idInputValue: action.payload,
      }

    case Actions.SET_ID_INPUT_VALUE:
      return {
        ...state,
        idInputValue: payload.setIdInputValue,
      }
    case Actions.GET_EDIT_FLAG:
      return {
        ...state,
        editFlag: true,
      }
    case Actions.RESET_EDIT_FLAG:
      return {
        ...state,
        editFlag: false,
      }
    case Actions.GET_ALERT_FLAG:
      return {
        ...state,
        alertFlag: true,
      }
    case Actions.RESET_ALERT_FLAG:
      return {
        ...state,
        alertFlag: false,
      }
    case Actions.COPY_SECTION_TO_SECTION_LIST:
      return {
        ...state,
        parts: action.payload,
      }
    case Actions.ADD_SECTION_TO_SECTION_LIST:
      return {
        ...state,
        parts: action.payload,
      }
    case Actions.DELETE_SECTION_FROM_SECTION_LIST:
      return {
        ...state,
        parts: action.payload,
      }
    case Actions.SET_SECTION_LIST_NAME:
      return {
        ...state,
        parts: payload,
      }
    case Actions.UPDATE_SECTION_LIST_VALUE:
      return {
        ...state,
        parts: payload.parts,
      }
    case Actions.GET_SECTION_LIST_INDEX:
      return {
        ...state,
        sectionListIndex: payload,
      }
    case Actions.ON_SELECTED_HEADER:
      return {
        ...state,
        header: payload,
      }
    case Actions.ON_SELECTED_FOOTER:
      return {
        ...state,
        footer: payload,
      }
    case Actions.TOGGLE_PAGE_STATUS:
      return {
        ...state,
        edit: {
          ...state.edit,
          ...payload.edit,
          layout_values: {
            ...state.edit.layout_values,
            ...payload.edit.layout_values,
          },
          meta: { ...state.edit.meta, ...payload.edit.meta },
        },
      }
    case Actions.CHANGE_ORIGINAL_TO_COMMON:
      return {
        ...state,
        parts: payload.parts,
        sections: payload.sections,
      }
    case Actions.FETCH_ALL_POST_TYPES:
      return {
        ...state,
        post_types: payload,
      }
    case Actions.FETCH_ALL_TAXONOMIES:
      return {
        ...state,
        taxonomies: payload,
      }
    case Actions.FETCH_ALL_FILTERED_POSTS:
      return {
        ...state,
        posts: payload,
      }
    case Actions.FETCH_ALL_CUSTOM_POSTS:
      return {
        ...state,
        custom_posts: payload,
      }
    case Actions.CHANGE_MOBILE_VIEW_MODE:
      return {
        ...state,
        mobileViewMode: {
          ...state.mobileViewMode,
          [action.payload]: !state.mobileViewMode[action.payload],
        },
      }
    case Actions.RESET_MOBILE_VIEW_MODE:
      return {
        ...state,
        mobileViewMode: {
          ...state.mobileViewMode,
          [action.payload]: false,
        },
      }
    default:
      return state
  }
}

const updateCondition = (state, value) => {
  return {
    ...state,
    stringSearch: value,
  }
}

export default UIPageReducer
